import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useState } from "react";
import { type UseFormReturn } from "react-hook-form";

import {
  type PostShiftReviewFormValues,
  type PostShiftReviewStep,
  QuestionTag,
  type StepAction,
  type StepActionData,
} from "../types";

interface UsePostShiftReviewNavigationProps {
  stepTypes: PostShiftReviewStep[];
  form: UseFormReturn<PostShiftReviewFormValues>;
  onSubmitRating?: (rating: number) => Promise<void>;
  onSubmitReview: (formData: PostShiftReviewFormValues) => Promise<void>;
  onComplete: () => void;
  closeModal: () => void;
  shiftId?: string;
}

export function usePostShiftReviewNavigation({
  stepTypes,
  form,
  onSubmitRating,
  onSubmitReview,
  onComplete,
  closeModal,
  shiftId,
}: UsePostShiftReviewNavigationProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSubmittingRatingLocally, setIsSubmittingRatingLocally] = useState(false);
  const [isSubmittingReviewLocally, setIsSubmittingReviewLocally] = useState(false);

  const currentStep = stepTypes[currentIndex];
  const isLastContentStep = currentIndex === stepTypes.length - 2; // Before "end" step

  function goNext() {
    setCurrentIndex((index) => Math.min(index + 1, stepTypes.length - 1));
  }

  function goBack() {
    setCurrentIndex((index) => Math.max(index - 1, 1));
  } // Never go back to rating step

  function goToEnd() {
    setCurrentIndex(stepTypes.length - 1);
  }

  function updateFormFromStepData(stepType: PostShiftReviewStep, data: StepActionData) {
    // FIXME: Automatically disabled during ESLint config update
    switch (stepType) {
      case "write-review": {
        if ("reviewText" in data) {
          form.setValue("reviewText", data.reviewText, { shouldValidate: true });
        }

        break;
      }

      case QuestionTag.SOFTWARE: {
        if ("answer" in data) {
          form.setValue("softwareAnswer", data.answer, { shouldValidate: true });
          if ("otherText" in data) {
            form.setValue("softwareOtherText", data.otherText, {
              shouldValidate: true,
            });
          }
        }

        break;
      }

      case QuestionTag.SUPPLIES: {
        if ("answer" in data) {
          form.setValue("suppliesAnswer", data.answer, { shouldValidate: true });
        }

        break;
      }

      case QuestionTag.PATIENTS_PER_NURSE: {
        if ("answer" in data) {
          form.setValue("patientCountAnswer", data.answer, { shouldValidate: true });
        }

        break;
      }

      case QuestionTag.PARKING: {
        if ("parkingAnswer" in data) {
          form.setValue("parkingAnswer", data.parkingAnswer, {
            shouldValidate: true,
          });
        }

        break;
      }

      default: {
        // No form updates needed for pick-rating or end steps
        break;
      }
    }
  }

  async function handleStepAction(action: StepAction) {
    switch (action.type) {
      case "SUBMIT_RATING": {
        if (action.rating && onSubmitRating) {
          form.setValue("rating", action.rating, { shouldValidate: true });
          setIsSubmittingRatingLocally(true);
          try {
            await onSubmitRating(action.rating);
            logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_CONTINUE_CLICKED, {
              step: "pick-rating",
              shiftId,
            });
            goNext();
          } finally {
            setIsSubmittingRatingLocally(false);
          }
        }

        break;
      }

      case "NEXT_STEP": {
        // Update form data from step
        if (action.data) {
          updateFormFromStepData(currentStep, action.data);
        }

        // Check if we're finishing the last content step
        if (isLastContentStep) {
          // Submit review with current form values
          const formData = form.getValues();
          setIsSubmittingReviewLocally(true);
          try {
            await onSubmitReview(formData);
            logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_CONTINUE_CLICKED, {
              step: currentStep,
              shiftId,
            });
            goToEnd();
          } finally {
            setIsSubmittingReviewLocally(false);
          }
        } else {
          logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_CONTINUE_CLICKED, {
            step: currentStep,
            shiftId,
          });
          goNext();
        }

        break;
      }

      case "BACK_STEP": {
        goBack();
        break;
      }

      case "SKIP_STEP": {
        goNext();
        break;
      }

      case "CLOSE_FLOW": {
        closeModal();
        onComplete();
        break;
      }

      case "SUBMIT_REVIEW": {
        const formData = form.getValues();
        setIsSubmittingReviewLocally(true);
        try {
          await onSubmitReview(formData);
          goToEnd();
        } finally {
          setIsSubmittingReviewLocally(false);
        }

        break;
      }

      default: {
        // No action needed for unrecognized action types
        break;
      }
    }
  }

  return {
    currentStep,
    currentIndex,
    handleStepAction,
    isSubmittingRatingLocally,
    isSubmittingReviewLocally,
    isFirst: currentIndex === 0,
    isLast: currentIndex === stepTypes.length - 1,
  };
}
